/* eslint-disable @typescript-eslint/member-delimiter-style */
import React, { createContext, useContext, ReactNode } from 'react'

import moment, { Moment } from 'config/moment'

interface DashboardFilters {
  // Date filters - clearer naming
  startPeriod1: Moment
  endPeriod1: Moment
  startPeriod2: Moment
  endPeriod2: Moment
  period: { month: number; year: number } // For tabs that use single period (legacy consumers)

  // Store filters
  store: string
  compareStore: string
  selectedStoreIds: number[]

  // Comparison flags
  compara: boolean

  // Active tab tracking
  activeTab: string

  // Trigger for applying filters
  applyTrigger: number

  // Loading state
  isLoading: boolean
}

interface DashboardFiltersContextType {
  filters: DashboardFilters
  handleStartPeriod1Change: (newDate: Moment | null) => void
  handleEndPeriod1Change: (newDate: Moment | null) => void
  handleStartPeriod2Change: (newDate: Moment | null) => void
  handleEndPeriod2Change: (newDate: Moment | null) => void
  handlePeriodChange: (newDate: Moment | null) => void
  handlePeriodSelect: (period: number) => void
  handleStoreChange: (e: React.ChangeEvent<{ value: unknown }>) => void
  handleCompareStoreChange: (e: React.ChangeEvent<{ value: unknown }>) => void
  handleSelectedStoresChange: (ids: number[]) => void
  handleComparaChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleApply: () => void
  setActiveTab: (tabKey: string) => void
  isActiveTab: (tabKey: string) => boolean
  setLoading: (loading: boolean) => void
}

const DashboardFiltersContext = createContext<DashboardFiltersContextType | undefined>(undefined)

interface DashboardFiltersProviderProps {
  children: ReactNode
}

export const DashboardFiltersProvider: React.FC<DashboardFiltersProviderProps> = ({ children }) => {
  // Default: Period 1 = primer día del mes actual hasta hoy
  // Period 2 = mes anterior completo
  const currentMonthStart = moment().startOf('month')
  const today = moment()
  const previousMonthStart = moment()
    .subtract(1, 'month')
    .startOf('month')
  const previousMonthEnd = moment()
    .subtract(1, 'month')
    .endOf('month')

  const [filters, setFilters] = React.useState<DashboardFilters>({
    startPeriod1: currentMonthStart,
    endPeriod1: today,
    startPeriod2: previousMonthStart,
    endPeriod2: previousMonthEnd,
    period: { month: currentMonthStart.month(), year: currentMonthStart.year() },
    store: 'all',
    compareStore: 'all',
    selectedStoreIds: [],
    compara: true, // Por defecto activado
    activeTab: 'overview',
    applyTrigger: 0,
    isLoading: false,
  })

  const handleStartPeriod1Change = (newDate: Moment | null) => {
    if (newDate) {
      setFilters(prev => {
        // Calculate duration based on new startPeriod1 and current endPeriod1
        const periodDuration = prev.endPeriod1.diff(newDate, 'days')
        // Calculate the same period for the previous month
        const newStartPeriod2 = newDate.clone().subtract(1, 'month')
        let newEndPeriod2 = newStartPeriod2.clone().add(periodDuration, 'days')

        // Ensure endPeriod2 doesn't exceed the maximum allowed date (startPeriod1 - 1 day)
        const maxAllowedDate = newDate.clone().subtract(1, 'day')
        if (newEndPeriod2.isAfter(maxAllowedDate)) {
          newEndPeriod2 = maxAllowedDate.clone()
        }

        return {
          ...prev,
          startPeriod1: newDate,
          period: { month: newDate.month(), year: newDate.year() },
          // Automatically update comparison period to same period of previous month
          startPeriod2: newStartPeriod2,
          endPeriod2: newEndPeriod2,
        }
      })
    }
  }

  const handleEndPeriod1Change = (newDate: Moment | null) => {
    if (newDate) {
      setFilters(prev => {
        // Calculate duration based on current startPeriod1 and new endPeriod1
        const periodDuration = newDate.diff(prev.startPeriod1, 'days')
        // Calculate the same period for the previous month
        const newStartPeriod2 = prev.startPeriod1.clone().subtract(1, 'month')
        let newEndPeriod2 = newStartPeriod2.clone().add(periodDuration, 'days')

        // Ensure endPeriod2 doesn't exceed the maximum allowed date (startPeriod1 - 1 day)
        const maxAllowedDate = prev.startPeriod1.clone().subtract(1, 'day')
        if (newEndPeriod2.isAfter(maxAllowedDate)) {
          newEndPeriod2 = maxAllowedDate.clone()
        }

        return {
          ...prev,
          endPeriod1: newDate,
          // Automatically update comparison period to same period of previous month
          startPeriod2: newStartPeriod2,
          endPeriod2: newEndPeriod2,
        }
      })
    }
  }

  const handleStartPeriod2Change = (newDate: Moment | null) => {
    if (newDate) {
      setFilters(prev => ({
        ...prev,
        startPeriod2: newDate,
      }))
    }
  }

  const handleEndPeriod2Change = (newDate: Moment | null) => {
    if (newDate) {
      setFilters(prev => ({
        ...prev,
        endPeriod2: newDate,
      }))
    }
  }

  const handlePeriodChange = (newDate: Moment | null) => {
    if (newDate) {
      const now = moment()
      const selectedMonth = newDate.clone().startOf('month')
      const isCurrentMonth =
        selectedMonth.year() === now.year() && selectedMonth.month() === now.month()

      setFilters(prev => ({
        ...prev,
        period: { month: newDate.month(), year: newDate.year() },
        startPeriod1: selectedMonth,
        endPeriod1: isCurrentMonth ? now.clone() : selectedMonth.clone().endOf('month'),
      }))
    }
  }

  const handleStoreChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    setFilters(prev => ({
      ...prev,
      store: e.target.value as string,
    }))
  }

  const handleCompareStoreChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    setFilters(prev => ({
      ...prev,
      compareStore: e.target.value as string,
    }))
  }

  const handleSelectedStoresChange = (ids: number[]) => {
    setFilters(prev => ({
      ...prev,
      selectedStoreIds: ids,
    }))
  }

  const handleComparaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({
      ...prev,
      compara: e.target.checked,
      compareStore: e.target.checked ? prev.compareStore : 'all',
    }))
  }

  const handlePeriodSelect = (period: number) => {
    const now = moment()
    let startPeriod1: Moment
    let endPeriod1: Moment
    let startPeriod2: Moment
    let endPeriod2: Moment

    if (period === 12) {
      // Últimos 30 días
      startPeriod1 = now.clone().subtract(30, 'days')
      endPeriod1 = now
      // Comparación: mismo período del mes anterior
      startPeriod2 = startPeriod1.clone().subtract(1, 'month')
      endPeriod2 = endPeriod1.clone().subtract(1, 'month')
    } else if (period === 13) {
      // Últimos 7 días
      startPeriod1 = now.clone().subtract(7, 'days')
      endPeriod1 = now
      // Comparación: mismo período del mes anterior
      startPeriod2 = startPeriod1.clone().subtract(1, 'month')
      endPeriod2 = endPeriod1.clone().subtract(1, 'month')
    } else if (period >= 0 && period < 600) {
      // Last 6 months: period format is monthsAgo * 100 + monthIndex
      // monthsAgo: 0 = current month, 1 = 1 month ago, etc.
      const monthsAgo = Math.floor(period / 100)
      const selectedDate = now.clone().subtract(monthsAgo, 'months')
      startPeriod1 = selectedDate.clone().startOf('month')
      // Si es el mes actual (monthsAgo === 0), usar hoy como fecha final
      // Si es un mes anterior, usar el último día del mes
      endPeriod1 = monthsAgo === 0 ? now.clone() : selectedDate.clone().endOf('month')
      // Comparación: mes anterior completo
      // Si es el mes actual, calcular el mismo período del mes anterior
      if (monthsAgo === 0) {
        const previousMonthDate = now.clone().subtract(1, 'month')
        startPeriod2 = previousMonthDate.clone().startOf('month')
        // Para el mes anterior, usar el último día del mes (no hoy del mes anterior)
        endPeriod2 = previousMonthDate.clone().endOf('month')
      } else {
        startPeriod2 = startPeriod1.clone().subtract(1, 'month')
        endPeriod2 = endPeriod1.clone().subtract(1, 'month')
      }
    } else {
      return
    }

    setFilters(prev => ({
      ...prev,
      startPeriod1,
      endPeriod1,
      startPeriod2,
      endPeriod2,
      period: { month: startPeriod1.month(), year: startPeriod1.year() },
    }))
  }

  const setActiveTab = (tabKey: string) => {
    setFilters(prev => ({
      ...prev,
      activeTab: tabKey,
      applyTrigger: 0, // Reset trigger when changing tabs
    }))
  }

  const isActiveTab = (tabKey: string) => {
    return filters.activeTab === tabKey
  }

  const handleApply = () => {
    setFilters(prev => ({
      ...prev,
      applyTrigger: prev.applyTrigger + 1,
      isLoading: true, // Set loading to true when applying
    }))
  }

  const setLoading = (loading: boolean) => {
    setFilters(prev => ({
      ...prev,
      isLoading: loading,
    }))
  }

  const value: DashboardFiltersContextType = {
    filters,
    handleStartPeriod1Change,
    handleEndPeriod1Change,
    handleStartPeriod2Change,
    handleEndPeriod2Change,
    handlePeriodChange,
    handlePeriodSelect,
    handleStoreChange,
    handleCompareStoreChange,
    handleSelectedStoresChange,
    handleComparaChange,
    handleApply,
    setActiveTab,
    isActiveTab,
    setLoading,
  }

  return (
    <DashboardFiltersContext.Provider value={value}>{children}</DashboardFiltersContext.Provider>
  )
}

export const useDashboardFilters = (): DashboardFiltersContextType => {
  const context = useContext(DashboardFiltersContext)
  if (context === undefined) {
    throw new Error('useDashboardFilters must be used within a DashboardFiltersProvider')
  }
  return context
}
